import { Injectable, Optional } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Flow } from '../obj/Flow';
import { FlowRun } from '../obj/FlowRun';
import { Connection } from '../obj/Connection';
import { ConfigurationItem, UserItem } from './storage-types';
import {
  EnvScanSource,
  StorageBackend,
  StorageMode,
  StoragePutOptions,
  RunScanQueueOptions,
  StoredEnvironment,
  StoreStats,
} from './storage-backend';
import { StorageOpfsSqliteService } from './storage-opfs-sqlite.service';
import { StorageNoCacheService } from './storage-no-cache.service';
import { StorageWorkerBackendService } from './storage-worker-backend.service';
import { StorageWorkerClientService } from './storage-worker-client.service';
import { StorageWorkerProbeResult } from './storage-worker.protocol';
import { BrowserCacheCoordinationService } from './browser-cache-coordination.service';
import { FlowEventService } from './flow-event.service';

/**
 * Storage facade. Historically routed between Dexie (legacy) and OPFS/SQLite;
 * Dexie has been removed and the router now delegates everything to the
 * OPFS-backed SQLite service. The class is kept (rather than deleted) so the
 * many existing call sites that inject it stay intact while the rename happens.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageRouterService implements StorageBackend {
  /**
   * Deprecated-tools-visible flag held in-memory at the storage layer for
   * convenience (the legacy Dexie service exposed it; keeping it here means
   * /route-root subscribers don't have to chase it across services).
   */
  public deprecatedToolsVisible$ = new BehaviorSubject<string[]>(['powerApps', 'approvals', 'httpRequest']);
  public storageWorkerProbe$ = new BehaviorSubject<StorageWorkerProbeResult | null>(null);
  public storageMode$ = new BehaviorSubject<StorageMode>('main-opfs');

  private backendPromise: Promise<StorageBackend>;
  private activeBackend: StorageBackend | null = null;
  private bridgeSubscriptions = new Map<string, Subscription>();
  private flowsSubject = new BehaviorSubject<Flow[]>([]);
  private flowsAdminSubject = new BehaviorSubject<Flow[]>([]);
  private runsSubject = new BehaviorSubject<FlowRun[]>([]);
  private usersSubject = new BehaviorSubject<UserItem[]>([]);
  private connectionsSubject = new BehaviorSubject<Connection[]>([]);

  constructor(
    private opfs: StorageOpfsSqliteService,
    private workerBackend: StorageWorkerBackendService,
    private storageWorkerClient: StorageWorkerClientService,
    private flowEventService: FlowEventService,
    private browserCacheCoordination?: BrowserCacheCoordinationService,
    @Optional() private noCache?: StorageNoCacheService,
  ) {
    this.backendPromise = this.selectBackend();
    this.browserCacheCoordination?.cacheInvalidated$.subscribe((event) => {
      this.refreshAfterCacheInvalidation(event.tables || []);
    });
  }

  getMode(): StorageMode {
    return this.activeBackend?.getMode() || this.storageMode$.value;
  }

  private async selectBackend(): Promise<StorageBackend> {
    try {
      this.reportStorageProgress('Storage worker starting', 'running');
      const result = await this.storageWorkerClient.probe();
      this.storageWorkerProbe$.next(result);
      if (!result) {
        console.log('[StorageRouterService] storage worker unavailable; probing main-thread OPFS');
        this.reportStorageProgress('Storage worker unavailable; using browser-thread cache', 'running');
        return this.selectMainThreadOrNoCache('storage worker unavailable');
      }
      console.log('[StorageRouterService] storage worker probe', result);
      if (!result.opfsAvailable || !result.sqliteProbeOk) {
        console.log('[StorageRouterService] storage worker OPFS/SQLite probe failed; probing main-thread OPFS', result.sqliteProbeError || '');
        this.reportStorageProgress('Storage worker probe failed; using browser-thread cache', 'running');
        this.storageWorkerClient.shutdown();
        return this.selectMainThreadOrNoCache(result.sqliteProbeError || 'storage worker OPFS/SQLite probe failed');
      }

      await this.workerBackend.ensureReady();
      console.log('[StorageRouterService] using worker-owned OPFS storage');
      this.reportStorageProgress('Using worker cache', 'done');
      return this.useBackend(this.workerBackend);
    } catch (error) {
      console.warn('[StorageRouterService] worker storage init failed; probing main-thread OPFS', error);
      this.reportStorageProgress('Storage worker timed out; using browser-thread cache', 'running');
      this.storageWorkerClient.shutdown();
      return this.selectMainThreadOrNoCache(String((error as any)?.message ?? error));
    }
  }

  private async selectMainThreadOrNoCache(reason: string): Promise<StorageBackend> {
    const probe = await this.opfs.probe();
    if (probe.ok) {
      console.log('[StorageRouterService] using main-thread OPFS storage');
      this.reportStorageProgress('Using browser-thread cache', 'done');
      return this.useBackend(this.opfs);
    }

    console.warn('[StorageRouterService] main-thread OPFS/SQLite unavailable; using no-cache storage', {
      reason,
      error: probe.error,
    });
    this.reportStorageProgress('Cache unavailable; using direct API only', 'failed');
    return this.useBackend(this.noCache || new StorageNoCacheService());
  }

  private reportStorageProgress(label: string, status: 'running' | 'done' | 'failed' | 'cancelled'): void {
    this.flowEventService.reportProgress({
      id: 'storage-mode',
      group: 'storage',
      label,
      status,
    });
  }

  private useBackend(backend: StorageBackend): StorageBackend {
    this.activeBackend = backend;
    this.storageMode$.next(backend.getMode());
    return backend;
  }

  private async getBackend(): Promise<StorageBackend> {
    return this.backendPromise;
  }

  private bridge<T>(key: string, sourceFactory: (backend: StorageBackend) => any, target: BehaviorSubject<T[]>): void {
    if (this.bridgeSubscriptions.has(key)) return;
    this.getBackend().then((backend) => {
      const subscription = sourceFactory(backend).subscribe((value: T[]) => target.next(value || []));
      this.bridgeSubscriptions.set(key, subscription);
    }).catch((error) => {
      console.warn(`[StorageRouterService] failed to bridge ${key}`, error);
    });
  }

  private refreshAfterCacheInvalidation(tables: string[]): void {
    const changed = new Set((tables || []).map((table) => String(table || '').toLowerCase()));
    this.getBackend().then((backend: any) => {
      if (changed.has('flows')) {
        backend.getFlows?.().then((flows: Flow[]) => this.flowsSubject.next(flows || [])).catch((error: any) => {
          console.warn('[StorageRouterService] cache invalidation flow refresh failed', error);
        });
        backend.getFlowsAdmin?.().then((flows: Flow[]) => this.flowsAdminSubject.next(flows || [])).catch((error: any) => {
          console.warn('[StorageRouterService] cache invalidation admin flow refresh failed', error);
        });
      }
      if (changed.has('runs')) {
        backend.getRuns?.(null).then((runs: FlowRun[]) => this.runsSubject.next(runs || [])).catch((error: any) => {
          console.warn('[StorageRouterService] cache invalidation runs refresh failed', error);
        });
      }
      if (changed.has('connections')) {
        backend.getConnections?.().then((connections: Connection[]) => this.connectionsSubject.next(connections || [])).catch((error: any) => {
          console.warn('[StorageRouterService] cache invalidation connections refresh failed', error);
        });
      }
    }).catch((error) => {
      console.warn('[StorageRouterService] cache invalidation refresh failed', error);
    });
  }

  async flush(): Promise<void> {
    return (await this.getBackend()).flush();
  }

  async getConfig(key: string): Promise<ConfigurationItem> {
    return (await this.getBackend()).getConfig(key);
  }

  getConfigs(keys: string[]): Promise<ConfigurationItem[]> {
    return Promise.all((keys || []).map((key) => this.getConfig(key)));
  }

  async putConfig(key: string, item: ConfigurationItem): Promise<any> {
    return (await this.getBackend()).putConfig(key, item);
  }

  async getFlows(): Promise<Flow[]> {
    return (await this.getBackend()).getFlows();
  }

  async getFlowSummaries(environment?: string): Promise<Flow[]> {
    return (await this.getBackend()).getFlowSummaries(environment);
  }

  async getFlowsAdmin(): Promise<Flow[]> {
    return (await this.getBackend()).getFlowsAdmin();
  }

  getFlows$() {
    this.bridge<Flow>('flows', (backend) => backend.getFlows$(), this.flowsSubject);
    return this.flowsSubject.asObservable();
  }

  getFlowsAdmin$() {
    this.bridge<Flow>('flowsAdmin', (backend) => backend.getFlowsAdmin$(), this.flowsAdminSubject);
    return this.flowsAdminSubject.asObservable();
  }

  async putFlow(flow: Flow, admin = false, options?: StoragePutOptions): Promise<any> {
    return (await this.getBackend()).putFlow(flow, admin, options);
  }

  async putFlows(flows: Flow[], admin = false, options?: StoragePutOptions): Promise<any> {
    return (await this.getBackend()).putFlows(flows, admin, options);
  }

  async removeFlows(flows: Flow[], admin = false): Promise<any> {
    return (await this.getBackend()).removeFlows(flows, admin);
  }

  async markRunScanQueued(environment: string, flowNames: string[], options?: RunScanQueueOptions): Promise<any> {
    return (await this.getBackend()).markRunScanQueued(environment, flowNames, options);
  }

  async clearRunScanQueued(environment: string, flowNames: string[]): Promise<any> {
    return (await this.getBackend()).clearRunScanQueued(environment, flowNames);
  }

  async putRuns(runs: FlowRun[]): Promise<any> {
    return (await this.getBackend()).putRuns(runs);
  }

  getRuns$(environment: string | null = null) {
    this.bridge<FlowRun>(`runs:${environment || ''}`, (backend) => backend.getRuns$(environment), this.runsSubject);
    return this.runsSubject.asObservable();
  }

  async getUsers(): Promise<UserItem[]> {
    return (await this.getBackend()).getUsers();
  }

  getUsers$() {
    this.bridge<UserItem>('users', (backend) => backend.getUsers$(), this.usersSubject);
    return this.usersSubject.asObservable();
  }

  async getUserByEmail(email: string): Promise<UserItem | undefined> {
    return (await this.getBackend()).getUserByEmail(email);
  }

  async putUsers(users: UserItem[]): Promise<any> {
    return (await this.getBackend()).putUsers(users);
  }

  // Connections cache --------------------------------------------------------

  async getConnections(environment?: string): Promise<Connection[]> {
    const backend: any = await this.getBackend();
    return backend.getConnections(environment);
  }

  getConnections$() {
    this.bridge<Connection>('connections', (backend: any) => backend.getConnections$(), this.connectionsSubject);
    return this.connectionsSubject.asObservable();
  }

  async putConnections(connections: Connection[], environment: string, scope: 'user' | 'admin'): Promise<any> {
    const backend: any = await this.getBackend();
    return backend.putConnections(connections, environment, scope);
  }

  async getEnvironment(name: string): Promise<StoredEnvironment | null> {
    return (await this.getBackend()).getEnvironment(name);
  }

  async getEnvironments(): Promise<StoredEnvironment[]> {
    return (await this.getBackend()).getEnvironments();
  }

  async putEnvironment(env: any): Promise<any> {
    return (await this.getBackend()).putEnvironment(env);
  }

  async putEnvironments(envs: any[]): Promise<any> {
    return (await this.getBackend()).putEnvironments(envs);
  }

  async markEnvScanCompleted(name: string, flowCount: number, durationMs: number): Promise<any> {
    return (await this.getBackend()).markEnvScanCompleted(name, flowCount, durationMs);
  }

  async markEnvSourceScanned(name: string, source: EnvScanSource, count?: number, durationMs?: number): Promise<any> {
    return (await this.getBackend()).markEnvSourceScanned(name, source, count, durationMs);
  }

  async getStoreStats(): Promise<StoreStats> {
    return (await this.getBackend()).getStoreStats();
  }

  async getAllConfigs(): Promise<{ key: string; value: any }[]> {
    return (await this.getBackend()).getAllConfigs();
  }

  async getAllUserAliases(): Promise<{ email: string; user_key: string }[]> {
    return (await this.getBackend()).getAllUserAliases();
  }

  async nukeStore(): Promise<void> {
    await (await this.getBackend()).nukeStore();
    this.flowsSubject.next([]);
    this.flowsAdminSubject.next([]);
    this.runsSubject.next([]);
    this.usersSubject.next([]);
    this.connectionsSubject.next([]);
  }

  /**
   * One-shot deletion of legacy Dexie IndexedDB databases. The old
   * StorageDexieService named its DB `fs-dx-${userOid}`, so any database
   * starting with `fs-dx-` is from the previous storage backend and is now
   * dead weight (and worse, can confuse browser-storage quotas).
   *
   * Idempotent and best-effort: any error is logged and swallowed. Browsers
   * that don't support `indexedDB.databases()` (older Safari) get a no-op.
   */
  async purgeLegacyDexieDatabases(): Promise<void> {
    try {
      const idb: any = (globalThis as any).indexedDB;
      if (!idb || typeof idb.databases !== 'function') return;
      const dbs: { name?: string }[] = await idb.databases();
      for (const db of dbs || []) {
        const name = db?.name;
        if (!name || !name.startsWith('fs-dx-')) continue;
        await new Promise<void>((resolve) => {
          const req = idb.deleteDatabase(name);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        });
      }
    } catch (err) {
      console.warn('[StorageRouterService] purgeLegacyDexieDatabases failed', err);
    }
  }
}
